import { Injectable } from '@angular/core';
import { ApiService } from '../../api.service';

export interface OpcaoAuditoria {
  valor: string;
  rotulo: string;
}

export interface FiltroAuditoriaPadrao {
  dataInicial: string;
  dataFinal: string;
  tipo: string;
  limite: number;
  nomeServidor: string;
  status: string;
}

@Injectable({ providedIn: 'root' })
export class ServicoAuditoriaAdministrativa {
  private readonly tipos: OpcaoAuditoria[] = [
    { valor: 'PONTO_REGISTRO', rotulo: 'Registro do ponto' },
    { valor: 'CONSULTA_CAPTURA_PONTO', rotulo: 'Consulta de captura facial' },
    { valor: 'FACE_IDENTIFICACAO_DECISAO', rotulo: 'Decisao facial' },
    { valor: 'BIOMETRIA_VALIDACAO', rotulo: 'Validacao biometrica' },
    { valor: 'FUNCIONARIO_EXCLUSAO', rotulo: 'Exclusao de funcionario' },
    { valor: 'PONTO_AJUSTE_MANUAL', rotulo: 'Ajuste manual de ponto' },
    { valor: 'EMPREGADOR_CADASTRO', rotulo: 'Cadastro de empregador' },
    { valor: 'EMPREGADOR_ATUALIZACAO', rotulo: 'Atualizacao de empregador' },
    { valor: 'EMPREGADOR_EXCLUSAO', rotulo: 'Exclusao de empregador' },
    { valor: 'JORNADA_CADASTRO', rotulo: 'Cadastro de jornada' },
    { valor: 'JORNADA_ATUALIZACAO', rotulo: 'Atualizacao de jornada' },
    { valor: 'JORNADA_EXCLUSAO', rotulo: 'Exclusao de jornada' },
    { valor: 'PONTO_FACIAL_IDENTIFICACAO', rotulo: 'Registro facial' },
    { valor: 'PONTO_OFFLINE_SINCRONIZACAO', rotulo: 'Sincronizacao offline' },
    { valor: 'BIOMETRIA_CACHE_OFFLINE', rotulo: 'Cache biometrico offline' },
    { valor: 'TERMINAL_ATIVACAO', rotulo: 'Ativacao de terminal' },
    { valor: 'TERMINAL_VALIDACAO', rotulo: 'Validacao de terminal' },
    { valor: 'TERMINAL_CADASTRO', rotulo: 'Cadastro de terminal' },
    { valor: 'TERMINAL_ATUALIZACAO', rotulo: 'Atualizacao de terminal' },
    { valor: 'TERMINAL_EXCLUSAO', rotulo: 'Exclusao de terminal' },
    { valor: 'TERMINAL_TOKEN_REVOGADO', rotulo: 'Revogacao de token' },
    { valor: 'AUTENTICACAO_LOGIN', rotulo: 'Login' },
    { valor: 'AUTENTICACAO_BOOTSTRAP', rotulo: 'Bootstrap de usuario' },
    { valor: 'USUARIO_CADASTRO', rotulo: 'Cadastro de usuario' },
    { valor: 'USUARIO_ATUALIZACAO', rotulo: 'Atualizacao de usuario' },
    { valor: 'USUARIO_EXCLUSAO', rotulo: 'Exclusao de usuario' },
    { valor: 'EXPORTACAO_PONTO', rotulo: 'Exportacao' }
  ];

  private readonly status: OpcaoAuditoria[] = [
    { valor: 'sucesso', rotulo: 'Sucesso' },
    { valor: 'falha', rotulo: 'Falha' },
    { valor: 'todos', rotulo: 'Todos' }
  ];

  constructor(private readonly api: ApiService) {}

  filtroPadrao(): FiltroAuditoriaPadrao {
    const hoje = this.dataParaIsoLocal(new Date());
    return {
      dataInicial: hoje,
      dataFinal: hoje,
      tipo: 'PONTO_REGISTRO',
      limite: 50,
      nomeServidor: '',
      status: 'sucesso'
    };
  }

  listar(
    empregadorId?: string,
    tipo?: string,
    limite = 50,
    dataInicial?: string,
    dataFinal?: string,
    nomeServidor?: string,
    pagina = 1,
    funcionarioSelecionado?: string,
    status?: string
  ) {
    return this.api.listarEventosAuditoria(
      empregadorId || undefined,
      tipo || undefined,
      limite,
      dataInicial || undefined,
      dataFinal || undefined,
      this.consultaServidor(nomeServidor, funcionarioSelecionado),
      pagina,
      this.consultaSucesso(status)
    );
  }

  consultaSucesso(status?: string) {
    if (status === 'sucesso') {
      return true;
    }
    if (status === 'falha') {
      return false;
    }
    return undefined;
  }

  consultaServidor(rotulo?: string, funcionarioSelecionado?: string) {
    if (!funcionarioSelecionado) {
      return undefined;
    }
    return this.nomeConsulta(rotulo);
  }

  nomeConsulta(nomeServidor?: string) {
    const nome = (nomeServidor || '').trim();
    return nome.length >= 2 ? nome : undefined;
  }

  mensagemErroCarregarEventos() {
    return 'Nao foi possivel carregar eventos de auditoria.';
  }

  mensagemErroCarregarCaptura(falhaForaHorario = false) {
    if (falhaForaHorario) {
      return 'Nao foi possivel abrir a captura desta tentativa. Recusas antigas nao guardam foto.';
    }
    return 'Nao foi possivel abrir a captura. Ajuste manual nao guarda foto.';
  }

  buscarCaptura(empregadorId: string, nsr: number) {
    return this.api.buscarCapturaPonto(empregadorId, nsr);
  }

  buscarCapturaFalha(empregadorId: string, eventoId: string) {
    return this.api.buscarCapturaFalhaPonto(empregadorId, eventoId);
  }

  avisoMiniaturaCaptura() {
    return 'Foto reduzida, so para comprovar a presenca no relogio.';
  }

  tiposAuditoria(): OpcaoAuditoria[] {
    return this.tipos;
  }

  statusAuditoria(): OpcaoAuditoria[] {
    return this.status;
  }

  rotuloTipo(tipo: string) {
    return this.tipos.find(item => item.valor === tipo)?.rotulo ?? tipo;
  }

  rotuloStatus(status: string) {
    return this.status.find(item => item.valor === status)?.rotulo ?? 'Todos';
  }

  dataIsoParaBr(dataIso: string) {
    const [ano, mes, dia] = dataIso.split('-');
    if (!ano || !mes || !dia) {
      return dataIso;
    }
    return `${dia}/${mes}/${ano}`;
  }

  nsrDoEvento(evento: { tipo?: string; sucesso?: boolean; descricao?: string } | null | undefined) {
    if (!evento?.sucesso || evento.tipo !== 'PONTO_REGISTRO') {
      return null;
    }
    const encontrado = (evento.descricao || '').match(/\bNSR\s+(\d+)\b/i);
    if (!encontrado) {
      return null;
    }
    return Number(encontrado[1]);
  }

  podeVerCaptura(evento: { tipo?: string; sucesso?: boolean; descricao?: string; temCapturaFacial?: boolean } | null | undefined) {
    return this.nsrDoEvento(evento) != null || !!evento?.temCapturaFacial;
  }

  private dataParaIsoLocal(data: Date) {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }
}
